import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, FileDown } from 'lucide-react';

/**
 * Componente de grupo colapsável para organizar serviços
 *
 * @param {string} title - Título do grupo
 * @param {number} count - Quantidade de itens no grupo
 * @param {React.ReactNode} children - Conteúdo do grupo
 * @param {boolean} defaultExpanded - Se deve iniciar expandido
 * @param {boolean} abrir - Pedido de fora para abrir (o serviço recém-criado está aqui dentro)
 * @param {string} valor - O dinheiro deste grupo, já formatado, ao lado da contagem
 * @param {string} valorTitulo - O que esse dinheiro significa neste grupo
 * @param {string} segundoValor - Um segundo dinheiro, de outra natureza
 * @param {function} onExport - Callback para exportar o grupo em PDF
 */
const CollapsibleServiceGroup = ({
  title,
  // Uma linha dizendo POR QUE o serviço está neste grupo. O título é o
  // verbo — "A cobrar" —, e o critério não caberia nele sem virar frase.
  subtitulo = null,
  count,
  children,
  defaultExpanded = true,
  abrir = false,
  valor = null,
  valorTitulo = '',
  segundoValor = null,
  onExport = null
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Quem acabou de criar um serviço precisa vê-lo. Os grupos nascem fechados
  // e o serviço novo caía dentro de um deles, invisível: "salvei e sumiu".
  // Abre quando pedido; o usuário continua podendo fechar depois.
  useEffect(() => { if (abrir) setIsExpanded(true); }, [abrir]);

  return (
    /* SEM overflow-hidden. Ele estava aqui só para os cantos arredondados, e
       cortava o menu ⋯ da última linha do grupo: o balão abria para baixo,
       passava da borda e sumia, com os itens inalcançáveis. Os cantos agora
       são do cabeçalho e do conteúdo, cada um arredondando o seu. */
    <div className="border rounded-lg">
      {/* Header Colapsável */}
      <div className={`w-full px-6 py-4 flex items-center justify-between border-b bg-slate-50 border-slate-200 rounded-t-lg ${
        isExpanded ? '' : 'rounded-b-lg'}`}>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
        >
          <span className="text-left">
            <h3 className="text-lg font-semibold text-slate-900 leading-tight">
              {title}
            </h3>
            {subtitulo && (
              <span className="block text-xs text-slate-500 font-normal">{subtitulo}</span>
            )}
          </span>
          <span className="px-3 py-1 rounded-full text-sm font-bold bg-slate-200 text-slate-700 tabular-nums">
            {count}
          </span>
          {/* O dinheiro do grupo, ao lado da contagem.
              O cabeçalho dizia só quantos serviços havia — numa aba de
              dinheiro, "A Faturar (3)" não responde a pergunta que fez a
              pessoa abrir a aba. O que esse número significa muda de grupo
              para grupo, e por isso vem de fora junto com o próprio texto. */}
          {valor && (
            <span className="text-sm font-semibold text-slate-900 tabular-nums" title={valorTitulo}>
              {valor}
            </span>
          )}
          {/* A segunda natureza, quando existe. Um grupo pode ter dinheiro
              FUTURO (a faturar, que depende de a etapa fechar) e dinheiro
              PRESENTE (entrada já emitida, com vencimento). Somar os dois num
              número só misturaria projeção com cobrança viva. */}
          {segundoValor && (
            <span className="text-xs text-slate-600 tabular-nums">{segundoValor}</span>
          )}
        </button>

        <div className="flex items-center space-x-3">
          {/* Botão de Exportar PDF */}
          {onExport && count > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onExport();
              }}
              className="p-2 rounded-lg text-slate-600 hover:bg-white hover:text-slate-900 transition-colors"
              title="Exportar para PDF"
            >
              <FileDown size={20} />
            </button>
          )}

          {/* Ícone de Expandir/Colapsar */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-slate-600 hover:text-slate-900 transition-colors"
          >
            {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </button>
        </div>
      </div>

      {/* Conteúdo */}
      {isExpanded && (
        <div className="bg-white rounded-b-lg">
          {count > 0 ? (
            children
          ) : (
            <div className="px-6 py-8 text-center text-slate-500">
              <p className="text-sm">Nenhum serviço nesta categoria</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CollapsibleServiceGroup;
